import {
  YARIKANO_CHAT_AUTO_QUESTION_ID,
  YARIKANO_CHAT_ITEMS,
  YARIKANO_CHAT_STORAGE_KEY,
} from '../constants/yarikanoChat';
import {
  EYarikanoChatLogKind,
  EYarikanoQuestionId,
  TYarikanoChatItem,
  TYarikanoChatLogEntry,
  TYarikanoChatSessionState,
} from '../types/yarikanoChat';
import parseJSON from './validateStorageVariable';

const QUESTION_IDS = new Set<string>(
  YARIKANO_CHAT_ITEMS.map((item) => item.id),
);

const emptySessionState = (): TYarikanoChatSessionState => ({
  consumedIds: [],
  log: [],
});

const isQuestionId = (value: unknown): value is EYarikanoQuestionId =>
  typeof value === 'string' && QUESTION_IDS.has(value);

const isLogEntry = (value: unknown): value is TYarikanoChatLogEntry => {
  if (!value || typeof value !== 'object') return false;
  const entry = value as Partial<TYarikanoChatLogEntry>;
  if (typeof entry.id !== 'string' || typeof entry.timetoken !== 'string') {
    return false;
  }
  if (!isQuestionId(entry.questionId)) return false;
  switch (entry.kind) {
    case EYarikanoChatLogKind.QUESTION:
    case EYarikanoChatLogKind.ANSWER_TEXT:
      return typeof entry.text === 'string';
    case EYarikanoChatLogKind.ANSWER_VIDEO:
      return typeof entry.videoUrl === 'string';
    case EYarikanoChatLogKind.ANSWER_CTA:
      return true;
    default:
      return false;
  }
};

const loadYarikanoChatSession = (): TYarikanoChatSessionState => {
  try {
    const raw = sessionStorage.getItem(YARIKANO_CHAT_STORAGE_KEY);
    if (!raw) return emptySessionState();
    const parsed = parseJSON(raw) as Partial<TYarikanoChatSessionState>;
    if (!parsed || !Array.isArray(parsed.consumedIds) || !Array.isArray(parsed.log)) {
      return emptySessionState();
    }
    const consumedIds = parsed.consumedIds.filter(isQuestionId);
    const log = parsed.log.filter(isLogEntry);

    if (
      consumedIds.length !== parsed.consumedIds.length ||
      log.length !== parsed.log.length ||
      consumedIds.some(
        (id) =>
          !log.some(
            (entry) =>
              entry.kind === EYarikanoChatLogKind.QUESTION &&
              entry.questionId === id,
          ),
      )
    ) {
      return emptySessionState();
    }

    return { consumedIds, log };
  } catch {
    return emptySessionState();
  }
};

const saveYarikanoChatSession = (state: TYarikanoChatSessionState): void => {
  try {
    sessionStorage.setItem(YARIKANO_CHAT_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage may be unavailable; keep in-memory UI state only
  }
};

const nextTimetoken = (baseMs: number, offset: number): string =>
  String((baseMs + offset) * 10000);

const createLogId = (prefix: string, questionId: string, index: number): string =>
  `${prefix}:${questionId}:${index}`;

/** Build question + answer log entries for one chip (no typing). */
const buildAnswerLogEntries = (
  item: TYarikanoChatItem,
  startedAtMs: number = Date.now(),
): TYarikanoChatLogEntry[] => {
  const entries: TYarikanoChatLogEntry[] = [];
  let offset = 0;

  entries.push({
    kind: EYarikanoChatLogKind.QUESTION,
    id: createLogId('q', item.id, 0),
    questionId: item.id,
    text: item.suggestText,
    timetoken: nextTimetoken(startedAtMs, offset++),
  });

  item.answerTexts.forEach((text, index) => {
    entries.push({
      kind: EYarikanoChatLogKind.ANSWER_TEXT,
      id: createLogId('at', item.id, index),
      questionId: item.id,
      text,
      timetoken: nextTimetoken(startedAtMs, offset++),
    });
  });

  if (item.videoUrl) {
    entries.push({
      kind: EYarikanoChatLogKind.ANSWER_VIDEO,
      id: createLogId('av', item.id, 0),
      questionId: item.id,
      videoUrl: item.videoUrl,
      timetoken: nextTimetoken(startedAtMs, offset++),
    });
  }

  entries.push({
    kind: EYarikanoChatLogKind.ANSWER_CTA,
    id: createLogId('ac', item.id, 0),
    questionId: item.id,
    timetoken: nextTimetoken(startedAtMs, offset++),
  });

  return entries;
};

const remainingManualChipItems = (
  consumedIds: EYarikanoQuestionId[],
): TYarikanoChatItem[] =>
  YARIKANO_CHAT_ITEMS.filter(
    (item) =>
      item.id !== YARIKANO_CHAT_AUTO_QUESTION_ID &&
      !consumedIds.includes(item.id),
  );

const areAllManualChipsConsumed = (
  consumedIds: EYarikanoQuestionId[],
): boolean => remainingManualChipItems(consumedIds).length === 0;

const shouldAutoSendGroup = (state: TYarikanoChatSessionState): boolean => {
  if (state.consumedIds.includes(YARIKANO_CHAT_AUTO_QUESTION_ID)) {
    return false;
  }
  return !state.log.some(
    (entry) =>
      entry.questionId === YARIKANO_CHAT_AUTO_QUESTION_ID &&
      entry.kind === EYarikanoChatLogKind.QUESTION,
  );
};

const appendConsumedAndLog = (
  state: TYarikanoChatSessionState,
  questionId: EYarikanoQuestionId,
  entries: TYarikanoChatLogEntry[],
): TYarikanoChatSessionState => {
  const consumedIds = state.consumedIds.includes(questionId)
    ? state.consumedIds
    : [...state.consumedIds, questionId];
  return {
    consumedIds,
    log: [...state.log, ...entries],
  };
};

export {
  emptySessionState,
  loadYarikanoChatSession,
  saveYarikanoChatSession,
  buildAnswerLogEntries,
  remainingManualChipItems,
  areAllManualChipsConsumed,
  shouldAutoSendGroup,
  appendConsumedAndLog,
};
